import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import { FormControl, Select, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type {
  ConditionEqualityType,
  ConditionFilterDescriptor,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../../api/types/svTypes.ts";
import BcDatePicker from "@entrilia/shared/components/reporting/bc-datepicker/BcDatePicker.tsx";
import type { RelativeDateValue } from "../../../../../../utils/relativeDates.ts";
import { equalityOptions, modeOptions, periodOptions } from "./dateFilterOptions.ts";
import type { DateMode } from "./dateFilterState.ts";
import { DateType } from "./dateFilterState.ts";
import ReferenceDateList from "./ReferenceDateList.tsx";
import { useDateFilter } from "./useDateFilter.ts";

interface Props {
  field: ConditionField;
  disableEqualitySelection?: boolean;
  disableReferenceDateSelection?: boolean;
  onFilterUpdated: (filter: ConditionFilterDescriptor, isValid: boolean) => void;
  onReferenceDateSelected: (filter: ConditionFilterDescriptor) => void;
}

const DateFilter = ({
  field,
  onFilterUpdated,
  onReferenceDateSelected,
  disableEqualitySelection,
  disableReferenceDateSelection,
}: Props) => {
  const {
    mode,
    periodType,
    dayEqualityType,
    exactDay,
    exactRange,
    reference,
    hidePeriodType,
    showModeToggle,
    showOperator,
    changeMode,
    changePeriodType,
    changeDayEqualityType,
    changeExactDay,
    changeExactRange,
    changeReference,
  } = useDateFilter({ field, disableEqualitySelection, disableReferenceDateSelection, onFilterUpdated });

  const handleReferenceDateSelect = (value: RelativeDateValue) => {
    onReferenceDateSelected(changeReference(value));
  };

  return (
    <Stack spacing={2} sx={{ p: "1rem", width: "100%" }}>
      {showModeToggle && (
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={mode}
          onChange={(_, value: DateMode | null) => {
            if (value !== null) {
              changeMode(value);
            }
          }}
        >
          {modeOptions.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.caption}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {mode === "exact" && !hidePeriodType && (
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={periodType}
          onChange={(_, value: DateType | null) => {
            if (value !== null) {
              changePeriodType(value);
            }
          }}
        >
          {periodOptions.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.caption}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {showOperator && (
        <FormControl size="small">
          <Select
            value={dayEqualityType}
            size="small"
            onChange={(e) => changeDayEqualityType(e.target.value as ConditionEqualityType)}
            sx={{ ".MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }}
          >
            {equalityOptions.map((v) => (
              <MenuItemStyled key={v.value} value={v.value}>
                <Typography>{v.caption}</Typography>
              </MenuItemStyled>
            ))}
          </Select>
        </FormControl>
      )}
      {mode === "exact" && periodType === DateType.Date && (
        <BcDatePicker value={exactDay} autoFocus onChange={(value) => changeExactDay(value)} />
      )}
      {mode === "exact" && periodType === DateType.Period && (
        <Stack direction="row" spacing={1}>
          <BcDatePicker
            value={exactRange[0]}
            maxDate={exactRange[1] ?? undefined}
            autoFocus
            onChange={(value) => changeExactRange([value, exactRange[1]])}
          />
          <BcDatePicker
            value={exactRange[1]}
            minDate={exactRange[0] ?? undefined}
            onChange={(value) => changeExactRange([exactRange[0], value])}
          />
        </Stack>
      )}
      {mode === "reference" && <ReferenceDateList selectedValue={reference} onSelect={handleReferenceDateSelect} />}
    </Stack>
  );
};

export default DateFilter;
